/**
 * AgentRegistryContext
 *
 * Global, session-scoped cache of full Agent records keyed by userProfileId.
 * Fetched lazily via agentServiceV2.readByUserProfileId with in-flight
 * dedup, so any number of consumers (EntryHeader, expand panels, menus) can
 * request the same agent without pounding the backend.
 *
 * Freshness: the agentUpdated RPC event upserts records in place, so edits
 * from any surface (library modal, inline header controls, other tabs)
 * propagate to all consumers.
 *
 * Memory: entries carry a fetchedAt timestamp and are lazily evicted on read
 * once older than TTL_MS. This bounds growth for long-lived tabs without a
 * background sweeper.
 */

import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useService, useServiceEventListener } from "@/hooks/useService";
import {
  AGENT_SERVICE_V2_IDENT,
  RPC_EVENT_AGENT_UPDATED_IDENT,
  type Agent,
  type AgentUpdatedEvent,
  type IAgentServiceV2,
} from "@bloomstack/apis/agentTypes";

/** Entries older than this are dropped on read and re-fetched on next request. */
const TTL_MS = 30 * 60 * 1000;

type CacheEntry = { agent: Agent; fetchedAt: number };

export type AgentRegistryContextType = {
  /** Synchronously read a cached agent. Undefined if not fetched or evicted. */
  get: (userProfileId: string) => Agent | undefined;
  /**
   * Ensure the agent for this profile is being fetched. Safe to call
   * repeatedly — dedupes in-flight requests.
   */
  request: (userProfileId: string) => void;
  /**
   * Apply a local optimistic update to a cached agent. Consumers that
   * mutate an agent (model select, thinking mode, budget) call this after
   * a successful update RPC so all consumers see the change immediately.
   */
  updateLocal: (userProfileId: string, partial: Partial<Agent>) => void;
};

const AgentRegistryContext = createContext<AgentRegistryContextType | null>(null);

export const AgentRegistryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const agentServiceV2 = useService<IAgentServiceV2>(AGENT_SERVICE_V2_IDENT);

  // Map identity changes on every write so context consumers re-render.
  const [cache, setCache] = useState<Map<string, CacheEntry>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());

  const upsert = useCallback((agent: Agent) => {
    if (!agent.userProfileId) return;
    setCache((prev) => {
      const next = new Map(prev);
      next.set(agent.userProfileId, { agent, fetchedAt: Date.now() });
      return next;
    });
  }, []);

  const get = useCallback(
    (userProfileId: string): Agent | undefined => {
      const entry = cache.get(userProfileId);
      if (!entry) return undefined;
      if (Date.now() - entry.fetchedAt > TTL_MS) {
        // Lazy eviction — drop stale entry, treat as a miss.
        setCache((prev) => {
          const next = new Map(prev);
          next.delete(userProfileId);
          return next;
        });
        return undefined;
      }
      return entry.agent;
    },
    [cache]
  );

  const request = useCallback(
    (userProfileId: string) => {
      const entry = cache.get(userProfileId);
      const fresh = entry && Date.now() - entry.fetchedAt <= TTL_MS;
      if (fresh) return;
      if (inFlightRef.current.has(userProfileId)) return;

      inFlightRef.current.add(userProfileId);
      agentServiceV2
        .readByUserProfileId(userProfileId)
        .then((res) => {
          if (res.success) {
            upsert(res.result);
          }
        })
        .catch(() => {
          // Silent — consumers just see no agent.
        })
        .finally(() => {
          inFlightRef.current.delete(userProfileId);
        });
    },
    [agentServiceV2, cache, upsert]
  );

  const updateLocal = useCallback((userProfileId: string, partial: Partial<Agent>) => {
    setCache((prev) => {
      const entry = prev.get(userProfileId);
      if (!entry) return prev;
      const next = new Map(prev);
      next.set(userProfileId, {
        agent: { ...entry.agent, ...partial },
        fetchedAt: Date.now(),
      });
      return next;
    });
  }, []);

  // Backend change events keep cached records fresh across all consumers.
  useServiceEventListener<AgentUpdatedEvent>(
    RPC_EVENT_AGENT_UPDATED_IDENT,
    (agent) => {
      upsert(agent);
    },
    [upsert]
  );

  const value = useMemo<AgentRegistryContextType>(
    () => ({ get, request, updateLocal }),
    [get, request, updateLocal]
  );

  return (
    <AgentRegistryContext.Provider value={value}>
      {children}
    </AgentRegistryContext.Provider>
  );
};

export function useAgentRegistry(throwIfMissing = true): AgentRegistryContextType | null {
  const ctx = useContext(AgentRegistryContext);
  if (throwIfMissing && !ctx) {
    throw new Error("useAgentRegistry must be used within an AgentRegistryProvider");
  }
  return ctx;
}

/**
 * Hook: resolve the agent for a userProfileId.
 *
 * Triggers a fetch on first call for a given id and returns the cached
 * record once resolved. Returns undefined while loading or if the profile
 * has no agent. Pass undefined to skip fetching (e.g. for user entries).
 *
 * The registry's map identity changes on writes, so consumers re-render
 * when the record lands or is updated.
 */
export function useAgentByProfileId(userProfileId: string | undefined): Agent | undefined {
  const registry = useAgentRegistry();

  useEffect(() => {
    if (userProfileId && registry) {
      registry.request(userProfileId);
    }
  }, [userProfileId, registry]);

  return userProfileId ? registry?.get(userProfileId) : undefined;
}
